import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';

import { GridPaginationModel, GridRowModel, GridSortModel } from '@mui/x-data-grid';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { PalmDataGrid } from 'common/components/PalmDataGrid';
import { selectPaginationGridData, selectPaginationMode, setPaginationGridData, setPaginationModel } from 'common/components/PalmDataGrid/palmDataGridSlice';
import { DISPLAYED_GRID_ROWS_COUNT } from 'common/constants/pagination';
import { PaginationMode } from 'common/constants/paginationMode';
import { Roles } from 'common/constants/roles';
import { Routes } from 'common/constants/routes';
import { usePalmButtonEvaluator } from 'common/hooks/usePalmButtonEvaluator';
import { usePalmFetchPaginatedEntities } from 'common/hooks/usePalmFetchPaginatedEntities';
import { usePalmFilters } from 'common/hooks/usePalmFilters';
import { usePalmSortModel } from 'common/hooks/usePalmSortModel';
import { fetchClaimUserMemberships, fetchClaimUserMembershipsForExport } from 'common/services/api/claimUserMembership/claimUserMembership.thunk';
import { ActionDefinition, ColumnType, Operations } from 'common/types/TypeDefs';
import { actionsColumnDefinition, userMembershipColumnsDefinitionWithoutActions } from 'common/utils/userMembershipColumnsHelper';

import { ClaimUserMembershipCreateDrawerContent } from './ClaimUserMembershipCreateDrawerContent';
import { ClaimUserMembershipDeleteDialogContent } from './ClaimUserMembershipDeleteDialogContent';
import { addNewFilter, clearFilters, removeFilter, selectFilteredStatus, selectFilters } from './claimUserMembershipSlice';
import { useClaimDropDownFiltersConfiguration } from './helpers';

export function ClaimUserMembershipTab() {
    const filteredStatus = useAppSelector(selectFilteredStatus);

    const shouldDisableButtons = usePalmButtonEvaluator([ Roles.Admin, Roles.AppManager, Roles.MultiTenantRO, Roles.UserManager ]);
    const shouldDisableCreateButton = usePalmButtonEvaluator([ Roles.Admin, Roles.UserManager ]);
    const shouldDisableEditButton = usePalmButtonEvaluator([ Roles.Admin, Roles.AppManager ]);
    const shouldDisableDeleteButton = usePalmButtonEvaluator([ Roles.Admin, Roles.UserManager ]);

    const [processing, setProcessing] = useState<boolean>(false);
    const dispatch = useAppDispatch();
    const paginationGridData = useAppSelector(selectPaginationGridData);
    const paginationMode = useAppSelector(selectPaginationMode) ?? PaginationMode.Server;
    const { suiteId = '' } = useParams();
    const [ defaultSortModel ] = usePalmSortModel('principalName');
    const filters = useAppSelector(selectFilters);

    const { tenantId = '', claimId = '' } = useParams();

    const fetchGridData = usePalmFetchPaginatedEntities(
        'Claim User Membership',
        { tenantId, claimId, status: filteredStatus, skip: 0, take: 0 },
        fetchClaimUserMemberships,
        defaultSortModel,
        setProcessing,
        selectFilters,
        [filters]
    );

    const filterProp = usePalmFilters(
        selectFilters,
        addNewFilter,
        removeFilter,
        clearFilters,
        useClaimDropDownFiltersConfiguration()
    );

    const fetchExportData = async (signal: AbortSignal, search?: string, model?: GridSortModel): Promise<GridRowModel[]> =>{
        return await dispatch(fetchClaimUserMembershipsForExport({
            tenantId: tenantId ?? '',
            claimId: claimId ?? '',
            search: search ?? '',
            sortKey: model?.[0]?.field,
            sortOrder: model?.[0]?.sort,
            rowsCount: paginationGridData?.rowsCount,
            signal,
            status: filteredStatus
        }))
            .unwrap()
            .then((response) => {
                return response;
            });
    };

    const refreshDataGrid = () => {
        fetchGridData(paginationGridData?.currentPage ?? 0, filters.keywords[0], paginationGridData?.sortModel ?? defaultSortModel);
    };

    const handlePageChange = (paginationModel: GridPaginationModel) => {
        dispatch(setPaginationModel(paginationModel));
        fetchGridData(paginationModel.page, filters.keywords[0], paginationGridData?.sortModel ?? defaultSortModel);
    };

    const handleSortModelChange = (model: GridSortModel) => {
        dispatch(setPaginationGridData({
            ...paginationGridData,
            sortModel: model,
        }));
        fetchGridData(paginationGridData?.currentPage ?? 0, filters.keywords[0], model);
    };

    const onGetRowLink = useCallback((userId: string) => {
        return `/${Routes.ApplicationSuites}/${suiteId}/${Routes.Tenants}/${tenantId}/${Routes.Users}/${userId}/${Routes.Claims}`;
    }, []);

    const actionDefinitions =  [
        { action: Operations.Open, disabled: shouldDisableButtons, urlToOpen: onGetRowLink },
        { action: Operations.Edit, disabled: shouldDisableEditButton },
        { action: Operations.Delete, disabled: shouldDisableDeleteButton }
    ] as ActionDefinition[];

    const claimValueColumn = { columnId: 'claimValue', columnName: 'Claim Value', columnType: ColumnType.Tooltip, copy: false };
    const columnDefinitions = userMembershipColumnsDefinitionWithoutActions.concat([claimValueColumn, actionsColumnDefinition]);

    return <PalmDataGrid
        title='User'
        processing={processing}
        actionDefinitions={actionDefinitions}
        columnDefinitions={columnDefinitions}
        paginationMode={paginationMode}
        isCreateButtonDisabled={shouldDisableCreateButton}
        filter={filterProp}
        refreshDataGridClick={refreshDataGrid}
        operationName={Operations.Link}
        contentFunction={ClaimUserMembershipCreateDrawerContent}
        deleteFunction={ClaimUserMembershipDeleteDialogContent}
        customDeleteTitle={'Are you sure you want to remove this User from this Claim?'}
        searchFilterDisabled={filters.keywords.length !== 0}
        paginationInfo={{
            handlePageChange,
            handleSortModelChange,
            take: DISPLAYED_GRID_ROWS_COUNT,
        }}
        getRowLink={shouldDisableButtons ? undefined : onGetRowLink}
        sortModel={paginationGridData?.sortModel ?? defaultSortModel}
        shouldShowExportButton={true}
        fetchExportData={fetchExportData}
    />;
}
